// @flow

import React, { useState, useEffect, useCallback } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { rem } from 'polished';
import { useDispatch, useSelector } from 'react-redux';
import { __ } from '../../../i18n/l10n';

import config from '../../../config/app.config.js';
import { HomeUrl, LoginUrl } from '../../../config/paths';
import { colors, fontFamily, media, sizes } from '../../../styles/GlobalStyle';
import type { RootState } from '../../../store/types';
import { WS_CLEAR_PENDING, WS_INIT_REGISTRATION } from '../../../store/actions';
import { Button, Modal } from '../../../modules/shared';

type LayerErrorRawProps = {
  className: string,
};

const LayerErrorRaw = ({ className }: LayerErrorRawProps) => {
  const websocket = useSelector((state: RootState) => state.websocket);
  const dispatch = useDispatch();
  const [layerError, setLayerError] = useState(websocket.layerError);
  const [secondsLeft, setSecondsLeft] = useState(config.recaptchaRedirectionSeconds);
  const [socketErrorModalIsOpen, setSocketErrorModalIsOpen] = useState(true);

  // Recaptcha setInterval
  useEffect(() => {
    setSocketErrorModalIsOpen(true);
    setLayerError(websocket.layerError);
    let timer;
    if (websocket.layerError && websocket.layerError.codeMessage === 'RECAPTCHA') {
      timer = setInterval(() => {
        setSecondsLeft((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [websocket.layerError]);

  const goToWinamaxRoot = () => {
    window.location.href = HomeUrl;
  };

  // Recaptcha redirection
  useEffect(() => {
    if (secondsLeft === 0) {
      goToWinamaxRoot();
    }
  }, [secondsLeft]);

  const clicButtonOk = () => {
    const disconnectUserErrorCode = [1403, 1404, 1405, 1406, 1411, 1444];
    if (layerError && disconnectUserErrorCode.includes(layerError.code)) {
      goToWinamaxRoot();
      return;
    }
    dispatch({ type: WS_CLEAR_PENDING });
  };

  const clicButtonRetry = useCallback(() => {
    dispatch({ type: WS_CLEAR_PENDING });
    if (!websocket.connected) {
      dispatch({
        type: WS_INIT_REGISTRATION,
      });
    } else if (websocket.layerError && websocket.layerError.retryAction) {
      dispatch(websocket.layerError.retryAction);
    }
  }, [dispatch, websocket.connected, websocket.layerError]);

  const clicWinamaxLogin = () => {
    window.location.href = LoginUrl;
  };

  const closeModal = useCallback(() => {
    dispatch({ type: WS_CLEAR_PENDING });
    setSocketErrorModalIsOpen(false);
  }, [dispatch]);

  const { body }: any = document;

  if (!layerError || websocket.backgroundAPI) {
    body.classList.remove('noscroll');
    return null;
  }

  body.classList.add('noscroll');

  if (layerError.codeMessage === 'WEBSOCKET') {
    return (
      <Modal isOpen={socketErrorModalIsOpen} onClose={closeModal} className="modalError">
        <div className={`${className} layerError--popin`}>
          <div className="layerError-cont">
            <span role="img" aria-label="" className="layerError-exclamation">
              ⚡
            </span>
            <div className="layerError-title">
              {layerError.isMainMessage ? __(layerError.message) : __('Oops,')}
            </div>
            {!layerError.isMainMessage && (
              <div className="layerError-text">{__(layerError.message)}</div>
            )}
            <Button type="submit" picto="arrow-right" onClick={clicButtonRetry}>
              {__('Try again')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (layerError.codeMessage === 'USER_ALREADY_EXISTS') {
    return (
      <Modal isOpen={socketErrorModalIsOpen} onClose={closeModal} className="modalError">
        <div className={`${className} layerError--popin`}>
          <div className="layerError-cont">
            <span role="img" aria-label="" className="layerError-exclamation">
              ⚡
            </span>
            <div className="layerError-title">
              {layerError.isMainMessage ? __(layerError.message) : __('Oops,')}
            </div>
            {!layerError.isMainMessage && (
              <div className="layerError-text">{__('Account already exist')}</div>
            )}
            <Button type="submit" picto="arrow-right" onClick={clicWinamaxLogin}>
              {__('Log in')}
            </Button>
            <Button
              className="under-button"
              type="button"
              theme="secondary"
              picto="edit"
              onClick={clicButtonOk}
            >
              {__('Edit email/phone number')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (layerError.codeMessage === 'ERROR_VALUES') {
    return (
      <Modal isOpen={socketErrorModalIsOpen} onClose={closeModal} className="modalError">
        <div className={`${className} layerError--popin`}>
          <div className="layerError-cont">
            <span role="img" aria-label="" className="layerError-exclamation">
              !
            </span>
            <div className="layerError-title">
              {layerError.isMainMessage ? __(layerError.message) : __('Oops,')}
            </div>
            {!layerError.isMainMessage && (
              <div className="layerError-text">{__(layerError.message, layerError.values)}</div>
            )}
            <Button type="submit" theme="secondary" picto="close" onClick={clicButtonOk}>
              {__('Ok')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <div className={className}>
      <div className="layerError-cont">
        <div className="layerError-exclamation">
          !
          <span />
        </div>
        <div className="layerError-title">{layerError.title ? layerError.title : __('Oops,')}</div>
        <div className="layerError-text">
          {layerError.message && __(layerError.message, secondsLeft)}
        </div>
        {layerError.buttons &&
          layerError.buttons.map((button, index) => (
            <Button
              key={button.picto}
              theme={button.theme || null}
              picto={button.picto || null}
              onClick={button.onClick || (() => {})}
              className={index > 0 ? 'under-button' : ''}
            >
              {__(button.title)}
            </Button>
          ))}
        {!layerError.buttons && layerError.retryAction && (
          <>
            <Button type="submit" picto="arrow-right" onClick={clicButtonRetry}>
              {__('Try again')}
            </Button>
            <br />
            <Button
              type="submit"
              theme="secondary"
              picto="close"
              onClick={layerError.codeMessage !== 'RECAPTCHA' ? clicButtonOk : goToWinamaxRoot}
            >
              {__('Ok')}
            </Button>
          </>
        )}
        {!layerError.buttons && !layerError.retryAction && (
          <Button type="submit" picto="arrow-right" onClick={clicButtonOk}>
            {__('Ok')}
          </Button>
        )}
      </div>
    </div>
  );
};

export const LayerError: StyledComponent<LayerErrorRawProps, {}, typeof LayerErrorRaw> = styled(
  LayerErrorRaw
)`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 12;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  padding: 30px;

  &.layerError--popin {
    background-color: transparent;
    position: relative;
    overflow-y: hidden;
    height: auto;
    margin-top: -70px;
    ${media.greaterThan('desktop')`
      margin-top: -130px;
    `}
  }

  .layerError-cont {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 400px;
  }

  .layerError-exclamation {
    font-family: ${fontFamily.secondary.regular.fontFamily};
    color: ${colors.yellow};
    font-size: ${rem(180)};
    height: 260px;
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 40px;
    span {
      width: 94px;
      height: 12px;
      border-radius: 50%;
      background-color: ${colors.darkGrey};
      display: inline-block;
      position: absolute;
      bottom: -10px;
      left: -5px;
    }
  }

  .layerError-title {
    font-family: ${fontFamily.secondary.regular.fontFamily};
    color: ${colors.yellow};
    font-size: ${rem(30)};
    margin-bottom: 20px;
  }

  .layerError-text {
    text-transform: uppercase;
    font-family: ${fontFamily.primary.black.fontFamily};
    font-weight: ${fontFamily.primary.black.fontWeight};
    font-size: ${rem(16)};
    margin-bottom: 65px;
    text-align: center;
  }

  .under-button {
    margin-top: calc(${sizes.padding} / 2);
    margin-bottom: calc(${sizes.padding} / 2 * -1);
  }
`;
